import {useSyncExternalStore} from "react";

/**
 * Temporary background tiles: an image uploaded in the sidebar becomes a new
 * gallery tile for the uploader only, until the page reloads (2026-10-13,
 * Sergey). "Save as default" makes it a real background of the product for
 * everyone (POST /api/v3/bg-list/first); nothing else persists it.
 *
 * Module state on purpose: it lives exactly as long as the page, and the
 * sidebar that adds a tile and the App that lists it share it without a
 * provider. Scoped per (channel, type, product), like the gallery itself.
 */
export interface SessionBgUpload {
  entryKey: string;
  kind: "upload";
  uploadId: string;
  sourceVariation: null;
  position: number;
  variation: number;
  imageUrl: string;
  thumbnailUrl: string;
  miniUrl: null;
  outdatedUrl: null;
  width: number | null;
  height: number | null;
  base: null;
  markers: string[];
  transforms: Record<string, unknown>;
  hasStoredTransforms: true;
}

const EMPTY: readonly SessionBgUpload[] = Object.freeze([]);
const scopes = new Map<string, readonly SessionBgUpload[]>();
const listeners = new Set<() => void>();

export const sessionBgScope = (
  channel: string | undefined,
  type: string | undefined,
  product: string | undefined
) => `${channel ?? ""}:${type ?? ""}:${product ?? ""}`;

function emit() {
  for (const listener of listeners) listener();
}

export function sessionBgUploads(scope: string): readonly SessionBgUpload[] {
  return scopes.get(scope) ?? EMPTY;
}

/** Add a tile (once per upload) at the end of the scope's temporary tiles. */
export function addSessionBgUpload(scope: string, entry: SessionBgUpload): void {
  const current = sessionBgUploads(scope);
  if (current.some((e) => e.entryKey === entry.entryKey)) return;
  scopes.set(scope, [...current, entry]);
  emit();
}

export function removeSessionBgUpload(scope: string, entryKey: string): void {
  const current = sessionBgUploads(scope);
  if (!current.some((e) => e.entryKey === entryKey)) return;
  const next = current.filter((e) => e.entryKey !== entryKey);
  if (next.length) scopes.set(scope, next);
  else scopes.delete(scope);
  emit();
}

export function findSessionBgUpload(scope: string, variation: unknown): SessionBgUpload | null {
  return sessionBgUploads(scope).find((e) => String(e.variation) === String(variation)) ?? null;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useSessionBgUploads(scope: string): readonly SessionBgUpload[] {
  return useSyncExternalStore(subscribe, () => sessionBgUploads(scope));
}

/** The scope's tiles after the list they extend, numbered on from its end. */
export function withSessionBgUploads<T extends {variation: number}>(
  list: readonly T[] | null | undefined,
  uploads: readonly SessionBgUpload[]
): Array<T | SessionBgUpload> | null | undefined {
  if (!list || uploads.length === 0) return list as T[] | null | undefined;
  const known = new Set(list.map((e) => e.variation));
  const extra = uploads
    .filter((u) => !known.has(u.variation))
    .map((u, i) => ({...u, position: list.length + i + 1}));
  return extra.length ? [...list, ...extra] : (list as T[]);
}
